import React from 'react';

import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem2 } from '@mui/x-tree-view/TreeItem2';
import { useTranslation } from 'react-i18next';
import { useGetReportsInWorkspaceQuery } from '../../api/power-bi/powerbiApiSlice';
import { getSelectedAccount } from '../../redux/selectedAccountSlice';
import { useSelector } from 'react-redux';
import items from '../../utils/navItems'; // Assumed import path
import { useGetSessionQuery } from '../../api/apiSlice';

import { useGetPortfolioAnalysisSubscriptionQuery } from '../../api/energy-analysis/portfolioAnalysisApiSlice';
import BoltIcon from '@mui/icons-material/Bolt';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
const NavigationItems = () => {
  const navigate = useNavigate();
  const selectedAccount = useSelector(getSelectedAccount);
  const { t } = useTranslation('glossary');

  const { data, error, isLoading, refetch } = useGetSessionQuery();
  const isAdmin = data?.claims?.['accounts.superuser'] === 'True' || false;

  const {
    data: monitorReports,
    isError: isMonitorError,
    isSuccess: isMonitorSuccess,
  } = useGetReportsInWorkspaceQuery(
    { accountId: selectedAccount.AccountId },
    { skip: !selectedAccount.AccountId },
  );
  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    isError: subscriptionError,
    isSuccess: subscriptionSuccess,
  } = useGetPortfolioAnalysisSubscriptionQuery(
    { recalc_account_id: selectedAccount?.AccountId },
    { skip: !selectedAccount?.AccountId },
  );

  const handleClick = (item) => {
    navigate(`${item?.path ? item.path : `/${item?.title}`}`);
  };

  const hasService = (item, services) => {
    if (item.service === undefined) return true;
    if (services?.some((s) => s.Name === item.service)) return true;
    return false;
  };

  return (
    <Box sx={{ minHeight: 352, minWidth: 295, px: 2, mt: 2.5 }}>
      <SimpleTreeView
        sx={{
          color: (theme) =>
            theme.palette.mode === 'light'
              ? 'hsl(0, 0%, 30%)'
              : 'hsl(0, 0%, 85%)',
        }}
      >
        {items.map((item) => {
          if (item.isAdmin && !isAdmin) return null;
          if (!hasService(item, selectedAccount?.Services) && !isAdmin)
            return null;
          if (item.title === 'monitor') {
            if (!isMonitorSuccess || monitorReports?.length === 0) return null;
            return (
              <CustomTreeItem
                key={item.title}
                itemId="monitor"
                slots={{ icon: () => item.icon }}
                label={t('monitor')}
              >
                {monitorReports?.map((report) => (
                  <CustomTreeItem
                    key={report.Id}
                    itemId={report.Id}
                    label={report.Name}
                    onClick={() =>
                      navigate('/powerbi', { state: { ReportId: report.Id } })
                    }
                  />
                ))}
              </CustomTreeItem>
            );
          }
          if (item.title === 'portfolio-analysis-hub') {
            return (
              <CustomTreeItem
                key={item.title}
                itemId="portfolio-analysis-hub"
                label={t(item.title)}
                slots={{ icon: () => item.icon }}
                slotProps={{
                  content: {
                    onClick: (e) => {
                      e.defaultMuiPrevented = true;
                      if (e.target.localName !== 'svg')
                        navigate('/portfolio-analysis-hub');
                    },
                  },
                }}
              >
                {!subscriptionError &&
                  [
                    { key: 'power', icon: <BoltIcon /> },
                    { key: 'heat', icon: <LocalFireDepartmentRoundedIcon /> },
                    { key: 'water', icon: <WaterDropRoundedIcon /> },
                  ].map((item) => {
                    const path = `/portfolio-analysis-hub/${item.key}/overview`;
                    return (
                      subscriptionData?.[item.key] && (
                        <CustomTreeItem
                          key={path}
                          itemId={path}
                          label={t(item.key)}
                          slots={{ icon: () => item.icon }}
                          onClick={() => navigate(path)}
                        />
                      )
                    );
                  })}
              </CustomTreeItem>
            );
          }
          return (
            <CustomTreeItem
              key={item.title}
              itemId={item.title}
              slots={{ icon: () => item.icon, endIcon: () => item.icon }}
              label={t(item.title)}
              onClick={() => handleClick(item)}
            />
          );
        })}
      </SimpleTreeView>
    </Box>
  );
};

export default NavigationItems;

import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import CircleIcon from '@mui/icons-material/Circle';
import { useTreeItem2Utils } from '@mui/x-tree-view/hooks';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

function CustomLabel({ children, status, onClick, onContentClick, ...props }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexGrow={1}
      {...props}
    >
      <Typography onClick={onContentClick}>{props.label}</Typography>
      <IconButton onClick={onClick} aria-label="select item" size="small">
        {status.expandable && status.expanded && (
          <ExpandMoreRoundedIcon fontSize="inherit" />
        )}
        {status.expandable && !status.expanded && (
          <KeyboardArrowRightRoundedIcon fontSize="inherit" />
        )}
      </IconButton>
    </Stack>
  );
}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
  const { interactions, status } = useTreeItem2Utils({
    itemId: props.itemId,
    children: props.children,
  });

  const handleIconButtonClick = (event) => {
    event.defaultMuiPrevented = true;
    interactions.handleExpansion(event);
  };

  const handleContentClick = (event) => {
    event.defaultMuiPrevented = true;
    interactions.handleSelection(event);
  };

  return (
    <TreeItem2
      {...props}
      ref={ref}
      slots={{
        ...props.slots,
        label: CustomLabel,
      }}
      slotProps={{
        label: {
          onClick: handleIconButtonClick,
          onContentClick: handleContentClick,
          status,
          label: props.label,
        },
        ...props.slotProps,
      }}
    />
  );
});
